/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Button, Empty, Modal, Typography } from 'antd';
import { FullscreenOutlined, InteractionOutlined } from '@ant-design/icons';
import AnalyticsTable from '../Tables/AnalyticsTable';
import EchartsWrapper from './EchartsWrapper';
import ChartLoader from './ChartLoader';

// Misc imports
import { formatChartData } from '../../utils/function/formatChartData';
import is from '../../utils/is';
import { formatNumberToThousandSeparator } from '../../utils/string/formatNumberToThousandSeparator';

const SummaryUserInteractionsSection = ({ data = {}, sectionTitle = '', seriesName = '', isLoading = true, renderOnlyChart = false, renderOnlyTable = false }) => {
  const [chartToFullscreen, setChartToFullscreen] = useState();
  const { t } = useTranslation();

  const getOptions = (chartData) => {
    const formattedData = formatChartData(chartData);
    const xData = formattedData.map(item => item.x);
    const yData = formattedData.map(item => item.y);

    return {
      grid: {
        left: '2%',
        right: '9%',
        bottom: '2%',
        containLabel: true
      },
      legend: {
        show: false
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          show: true,
          type: 'none'
        },
        triggerOn: 'mousemove|click',
        enterable: true,
        renderMode: 'html',
        confine: true,
        formatter: (params) => {
          const { name, value, marker } = params[0];
          return `${name}<br />${marker} ${seriesName}: ${formatNumberToThousandSeparator(value)}`;
        }
      },
      xAxis: [{
        type: 'category',
        position: 'bottom',
        axisTick: {
          show: true,
          alignWithLabel: true
        },
        axisLine: {
          show: false
        },
        axisLabel: {
          show: true,
          // How many values will be skipped between labels in the X Axis
          // e.x For 10 dates, this will write the first date, skip the next 8, and write the last
          interval: xData?.length - 2
        },
        data: xData
      }],
      yAxis: [
        {
          type: 'value'
        }
      ],
      series: [
        {
          name: seriesName,
          type: 'bar',
          data: yData
        }
      ],
      toolbox: {
        show: !renderOnlyChart && chartData?.length > 0,
        feature: {
          magicType: {
            type: [t('line'), t('bar')]
          }
        }
      }
    };
  };

  const tableConfig = {
    descriptionTableData: data,
    isDescriptionTable: true,
    isTableLoading: isLoading,
    emptyLines: 1,
    descriptionTableFields: [
      {
        label: t('Total user interactions'),
        dataField: 'total',
        isPercentage: false,
        isToBeFixed: false
      },
      {
        label: t('Session average'),
        dataField: 'avgPerSession',
        isPercentage: false,
        isToBeFixed: true
      },
      {
        label: t('User average'),
        dataField: 'avgPerUser',
        isPercentage: false,
        isToBeFixed: true
      }
    ]
  };

  const chart = (
    <Fragment>
      {
        data?.histogram?.length > 0
          ? <EchartsWrapper
            chartOptions={getOptions(data.histogram)}
            style={{ height: 367 }}
            loading={isLoading}
          />
          : <div className="d-flex content-fill flex-column h-100">
            <div className="mt-5 d-flex align-items-center mb-auto text-center flex-auto align-self-center">
              <Empty
                description={(
                  <Typography.Paragraph>
                    {t('No data available for this chart')}
                  </Typography.Paragraph>
                )}
              />
            </div>
          </div>
      }
    </Fragment>
  );
  return (
    <Fragment>
      {
        renderOnlyChart
          ? chart
          : <Fragment>
            {
              !renderOnlyTable
                ? <Fragment>
                  <div className="mb-6 position-relative">
                    <Typography.Title level={4} className="mb-6">
                      <Typography.Text className="mr-2">{sectionTitle}</Typography.Text>
                      <InteractionOutlined className="ml-2" />
                    </Typography.Title>

                    {isLoading ? <ChartLoader /> : chart}

                    <Button
                      type="text"
                      size="large"
                      disabled={isLoading}
                      icon={<FullscreenOutlined />}
                      className="position-absolute top-0 right-0"
                      onClick={() => setChartToFullscreen(chart)}
                    />
                  </div>

                  <AnalyticsTable
                    descriptionTableData={tableConfig.descriptionTableData}
                    isDescriptionTable={tableConfig.isDescriptionTable}
                    isTableLoading={tableConfig.isTableLoading}
                    emptyLines={tableConfig.emptyLines}
                    descriptionTableFields={tableConfig.descriptionTableFields}
                  />

                  <Modal
                    open={is.truthy(chartToFullscreen)}
                    onCancel={() => setChartToFullscreen()}
                    destroyOnClose
                    footer={null}
                    centered
                    style={{ maxWidth: 1000 }}
                    width={'95%'}
                  >
                    <div className="p-5">
                      {chartToFullscreen}
                    </div>
                  </Modal>
                </Fragment>
                : <AnalyticsTable
                  descriptionTableData={tableConfig.descriptionTableData}
                  isDescriptionTable={tableConfig.isDescriptionTable}
                  isTableLoading={tableConfig.isTableLoading}
                  emptyLines={tableConfig.emptyLines}
                  descriptionTableFields={tableConfig.descriptionTableFields}
                />
            }
          </Fragment>
      }
    </Fragment>
  );
};

SummaryUserInteractionsSection.propTypes = {
  data: PropTypes.object,
  sectionTitle: PropTypes.string,
  seriesName: PropTypes.string,
  isLoading: PropTypes.bool,
  renderOnlyChart: PropTypes.bool,
  renderOnlyTable: PropTypes.bool
};

export default SummaryUserInteractionsSection;
